import styled, { keyframes } from "styled-components";

const floatOrb = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -20px) scale(1.05); }
  66% { transform: translate(-20px, 15px) scale(0.95); }
`;

const routeMove = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(50%); }
`;

const routeDot = keyframes`
  0% { left: -2%; opacity: 0; }
  5% { opacity: 0.3; }
  95% { opacity: 0.3; }
  100% { left: 102%; opacity: 0; }
`;

const floatIcon = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.5; }
  25% { transform: translateY(-15px) rotate(3deg); opacity: 1; }
  50% { transform: translateY(5px) rotate(-2deg); opacity: 0.7; }
  75% { transform: translateY(-8px) rotate(1deg); opacity: 0.9; }
`;

export const LoginScreen = styled.div`
  display: flex;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: #f0f4f8;
`;

export const LoginBg = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    top: -30%;
    right: -20%;
    width: 700px;
    height: 700px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(33,63,154,0.06) 0%, transparent 70%);
    animation: ${floatOrb} 20s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -20%;
    left: -15%;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(114,197,70,0.06) 0%, transparent 70%);
    animation: ${floatOrb} 25s ease-in-out infinite reverse;
  }
`;

export const LoginGrid = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(33,63,154,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(33,63,154,0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  z-index: 1;
`;

export const LoginRoutes = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  opacity: 0.4;
`;

export const RouteLine = styled.div`
  position: absolute;
  height: 2px;
  left: -50%;
  width: 200%;
  background: linear-gradient(90deg, transparent, rgba(33,63,154,0.15), rgba(114,197,70,0.15), transparent);
  animation: ${routeMove} ${({ duration }) => duration || '12s'} linear infinite;
  animation-delay: ${({ delay }) => delay || '0s'};
  top: ${({ top }) => top};
`;

export const RouteDotEl = styled.div`
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ green }) => green ? '#72C546' : '#213f9a'};
  opacity: 0.2;
  animation: ${routeDot} ${({ duration }) => duration || '12s'} linear infinite;
  animation-delay: ${({ delay }) => delay || '0s'};
  top: ${({ top }) => top};
`;

export const FloatIcons = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
`;

export const FloatIcon = styled.span`
  position: absolute;
  color: ${({ green }) => green ? 'rgba(114,197,70,0.06)' : 'rgba(33,63,154,0.06)'};
  font-size: ${({ size }) => size || '48px'};
  animation: ${floatIcon} 20s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || '0s'};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
`;

export const LoginLayout = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  position: relative;
  z-index: 2;
`;

export const BrandPanel = styled.div`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: ${({ theme }) => theme.space[8]};

  @media (min-width: 769px) {
    display: flex;
  }
`;

export const BrandLogo = styled.img`
  width: 260px;
  margin-bottom: ${({ theme }) => theme.space[8]};
  filter: brightness(0) saturate(100%) invert(19%) sepia(72%) saturate(1800%) hue-rotate(218deg) brightness(85%) contrast(95%);
  opacity: 0.9;
`;

export const BrandTagline = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  line-height: 1.2;
  letter-spacing: -1px;
  margin-bottom: ${({ theme }) => theme.space[4]};
  max-width: 380px;

  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.success} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const BrandDesc = styled.p`
  font-size: ${({ theme }) => theme.fonts.base};
  color: ${({ theme }) => theme.colors.gray500};
  text-align: center;
  max-width: 340px;
  line-height: 1.6;
`;

export const BrandFeatures = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[6]};
  margin-top: ${({ theme }) => theme.space[8]};
`;

export const BrandFeature = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]};

  span {
    font-size: ${({ theme }) => theme.fonts.xs};
    color: ${({ theme }) => theme.colors.gray500};
    font-weight: 500;
  }
`;

export const BrandFeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: rgba(33,63,154,0.06);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 24px;
`;

export const FormPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.space[6]};

  @media (min-width: 769px) {
    max-width: 480px;
    flex-shrink: 0;
    background: rgba(255,255,255,0.5);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }
`;

export const LoginCard = styled.div`
  width: 100%;
  max-width: 380px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radius.xl};
  padding: ${({ theme }) => theme.space[8]} ${({ theme }) => theme.space[7]};
  box-shadow: 0 0 0 1px rgba(33,63,154,0.04), 0 4px 6px rgba(0,0,0,0.03), 0 12px 24px rgba(33,63,154,0.06);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.successHover}, ${({ theme }) => theme.colors.primary});
    border-radius: ${({ theme }) => theme.radius.xl} ${({ theme }) => theme.radius.xl} 0 0;
  }

  @media (min-width: 769px) {
    box-shadow: 0 0 0 1px rgba(33,63,154,0.06), 0 8px 16px rgba(0,0,0,0.04), 0 20px 40px rgba(33,63,154,0.08);
  }
`;

export const CardLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space[6]};

  img {
    height: 40px;
    filter: brightness(0) saturate(100%) invert(19%) sepia(72%) saturate(1800%) hue-rotate(218deg) brightness(85%) contrast(95%);
  }
`;

export const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space[7]};
`;

export const IconBadge = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto ${({ theme }) => theme.space[5]};
  background: linear-gradient(135deg, rgba(33,63,154,0.08) 0%, rgba(114,197,70,0.08) 100%);
  border-radius: ${({ theme }) => theme.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 28px;
`;

export const LoginTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primaryDark};
  margin-bottom: ${({ theme }) => theme.space[1]};
`;

export const LoginSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fonts.sm};
`;

export const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.space[4]};
  text-align: left;
`;

export const FormLabel = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: ${({ theme }) => theme.fonts.sm};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.space[2]};
`;

export const FormInputWrapper = styled.div`
  position: relative;
`;

export const FormInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.space[3]} ${({ theme }) => theme.space[4]};
  padding-left: ${({ theme }) => theme.space[9]};
  background: ${({ theme }) => theme.colors.gray50};
  border: 1.5px solid ${({ theme, error }) => error ? theme.colors.error : theme.colors.gray200};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.gray800};
  font-size: ${({ theme }) => theme.fonts.md};
  transition: ${({ theme }) => theme.transitions.fast};

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray400};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.white};
    box-shadow: 0 0 0 3px rgba(33,63,154,0.08);
  }
`;

export const FormInputIcon = styled.span`
  position: absolute;
  left: ${({ theme }) => theme.space[3]};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.gray400};
  pointer-events: none;
  font-size: 20px;
  display: flex;
`;

export const FormError = styled.div`
  display: ${({ visible }) => visible ? 'flex' : 'none'};
  align-items: center;
  gap: ${({ theme }) => theme.space[2]};
  margin-top: ${({ theme }) => theme.space[2]};
  padding: ${({ theme }) => theme.space[2]} ${({ theme }) => theme.space[3]};
  background: ${({ theme }) => theme.colors.errorLight};
  border: 1px solid rgba(220, 53, 69, 0.15);
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fonts.sm};
`;

export const BtnLogin = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.space[3]} ${({ theme }) => theme.space[6]};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryLight} 100%);
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fonts.md};
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-top: ${({ theme }) => theme.space[4]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space[2]};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33,63,154,0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const LoginDivider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[3]};
  margin: ${({ theme }) => theme.space[5]} 0;
  color: ${({ theme }) => theme.colors.gray400};
  font-size: ${({ theme }) => theme.fonts.xs};

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.gray200};
  }
`;

export const LoginFooter = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.space[6]};
  padding-top: ${({ theme }) => theme.space[5]};
  border-top: 1px solid ${({ theme }) => theme.colors.gray100};
`;

export const FooterText = styled.div`
  font-size: ${({ theme }) => theme.fonts.xs};
  color: ${({ theme }) => theme.colors.gray400};
`;

export const FooterBrand = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space[2]};
  margin-top: ${({ theme }) => theme.space[2]};

  img {
    height: 16px;
    filter: brightness(0) saturate(100%) invert(50%);
    opacity: 0.6;
  }

  span {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.gray400};
  }
`;
